// ============================================================================
// Widget — Case Flow (New vs Closed)
// Per-month counts of cases opened vs closed across ALL case types. Net delta
// in the subtitle tells management whether we're keeping up with intake.
// ============================================================================
import React from 'react';
import ReactECharts from 'echarts-for-react';
import WidgetShell from './WidgetShell';
import { mockCaseFlow, fmtCount } from '../executiveMockData';

const CaseFlowWidget: React.FC = () => {
  const labels  = mockCaseFlow.map(p => p.period);
  const opened  = mockCaseFlow.map(p => p.newCases);
  const closed  = mockCaseFlow.map(p => p.closedCases);

  const totalNew    = opened.reduce((a, b) => a + b, 0);
  const totalClosed = closed.reduce((a, b) => a + b, 0);
  const netDelta    = totalNew - totalClosed;
  const closureRate = totalClosed / Math.max(totalNew, 1) * 100;

  const option = {
    grid: { left: 40, right: 16, top: 24, bottom: 28 },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any[]) => {
        const idx = params[0].dataIndex;
        const p = mockCaseFlow[idx];
        const net = p.newCases - p.closedCases;
        const netColor = net > 0 ? '#dc2626' : '#16a34a';
        return `<strong>${p.period}</strong><br/>
                New: ${fmtCount(p.newCases)}<br/>
                Closed: ${fmtCount(p.closedCases)}<br/>
                Net: <span style="color:${netColor}">${net > 0 ? '+' : ''}${net}</span>`;
      },
    },
    legend: { show: true, top: 0, right: 0, fontSize: 11, itemHeight: 8, itemWidth: 14 },
    xAxis: { type: 'category', data: labels, axisTick: { show: false }, axisLabel: { fontSize: 11 } },
    yAxis: {
      type: 'value', name: 'Cases',
      nameTextStyle: { fontSize: 10, color: '#64748b' },
      axisLabel: { fontSize: 10 },
      splitLine: { lineStyle: { color: '#e5e7eb', type: 'dashed' } },
    },
    series: [
      {
        name: 'New',
        type: 'bar',
        barGap: 0,
        barWidth: 14,
        itemStyle: { color: '#2563eb', borderRadius: [3, 3, 0, 0] },
        data: opened,
      },
      {
        name: 'Closed',
        type: 'bar',
        barWidth: 14,
        itemStyle: { color: '#16a34a', borderRadius: [3, 3, 0, 0] },
        data: closed,
      },
    ],
  };

  return (
    <WidgetShell
      title="Case Flow — New vs Closed"
      subtitle={`6 months: ${fmtCount(totalNew)} opened · ${fmtCount(totalClosed)} closed · closure rate ${closureRate.toFixed(0)}% · net ${netDelta > 0 ? '+' : ''}${netDelta}`}
    >
      <ReactECharts option={option} style={{ height: 220, width: '100%' }} />
    </WidgetShell>
  );
};

export default CaseFlowWidget;
